import { ApolloError, useLazyQuery } from '@apollo/client'
import { APIStatus } from '@crimeos-frontend/shared/api-common'

import { ContentDisposition } from '@crimeos-frontend/crime-os/graphql'
import {
  useCustomT,
  convertToErrorType,
  getAPIStatusFromNetworkStatus
} from '@crimeos-frontend/shared/common'

import { showFailureToastWithGivenMessage } from '../../../utils/ToastUtils'

import {
  GetTaskAttachmentSignedReadUrlDocument as QueryDocument,
  GetTaskAttachmentSignedReadUrlQuery as Query,
  GetTaskAttachmentSignedReadUrlQueryVariables as QueryVariables
} from './query.generatedTypes'
import { useAPIResponseHandler } from './responseHandler'

interface TriggerAPIProps {
  taskId: number
  attachmentId: number
  contentDisposition: ContentDisposition
  onSuccess?: (url: string) => void
  onFailure?: (message: string) => void
}

interface APIHookReturnType {
  triggerAPI: (args: TriggerAPIProps) => void
  apiStatus: APIStatus
  apiError: Error | null
}

const useGetTaskAttachmentSignedReadUrl = (): APIHookReturnType => {
  const apiErrorMessagesT = useCustomT('common:apiErrorMessages')
  const responseHandler = useAPIResponseHandler()

  const [getTaskAttachmentSignedReadUrl, { data, error, networkStatus }] =
    useLazyQuery<Query, QueryVariables>(QueryDocument)

  const triggerAPI = (args: TriggerAPIProps): void => {
    const requestObject = {
      params: {
        taskId: args.taskId,
        attachmentId: args.attachmentId,
        contentDisposition: args.contentDisposition
      }
    }

    getTaskAttachmentSignedReadUrl({
      variables: requestObject,
      onCompleted: (response: Query) => {
        responseHandler.handleAPIResponse(response, args.onSuccess)
      },
      onError: (apolloError: ApolloError) => {
        const error = convertToErrorType(apolloError)
        const errorMessage =
          error?.message || apiErrorMessagesT('somethingWentWrong')

        args.onFailure
          ? args.onFailure(errorMessage)
          : showFailureToastWithGivenMessage(errorMessage)
      }
    })
  }

  const apiStatus = getAPIStatusFromNetworkStatus(networkStatus, data)
  const apiError = convertToErrorType(error)

  return {
    triggerAPI,
    apiStatus,
    apiError
  }
}

export default useGetTaskAttachmentSignedReadUrl
